/**
 * SessionSummaryCard — Displays post-call intelligence after a voice session ends.
 *
 * Shows: key topics, action items (with optional Create Issue buttons),
 * session duration, and sentiment indicator.
 */

import { useState, useEffect } from 'react';
import { Clock, CheckCircle2, AlertCircle, MessageSquare, ChevronDown, ChevronUp, X } from 'lucide-react';

interface ActionItem {
  text: string;
  source: 'extraction' | 'transcript';
  confidence: number;
}

interface SessionSummaryData {
  conversation_id: string;
  duration_secs: number;
  summary: string | null;
  action_items: ActionItem[];
  success_score: number | null;
  sentiment: string | null;
}

interface SessionSummaryCardProps {
  conversationId: string;
  onDismiss?: () => void;
}

export function SessionSummaryCard({ conversationId, onDismiss }: SessionSummaryCardProps) {
  const [data, setData] = useState<SessionSummaryData | null>(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(true);

  useEffect(() => {
    if (!conversationId) return;

    const fetchSummary = async () => {
      try {
        const res = await fetch(`/api/v1/talk/session/${conversationId}/summary`, {
          credentials: 'include',
        });
        if (res.ok) {
          setData(await res.json());
        }
      } catch {
        // Summary not available yet
      } finally {
        setLoading(false);
      }
    };

    // Poll briefly — webhook may arrive slightly after session ends
    const timer = setTimeout(fetchSummary, 2000);
    return () => clearTimeout(timer);
  }, [conversationId]);

  if (loading || !data) return null;

  const sentimentColor = data.sentiment === 'positive' ? 'text-emerald-600' :
    data.sentiment === 'negative' ? 'text-red-500' : 'text-amber-500';

  const sentimentIcon = data.sentiment === 'positive' ? CheckCircle2 :
    data.sentiment === 'negative' ? AlertCircle : MessageSquare;

  const SentimentIcon = sentimentIcon;

  const minutes = Math.floor(data.duration_secs / 60);
  const seconds = Math.round(data.duration_secs % 60);

  return (
    <div className="mx-3 my-2 rounded-xl border border-coral-200/60 bg-coral-50/30 overflow-hidden">
      {/* Header */}
      <button
        onClick={() => setExpanded(v => !v)}
        className="w-full flex items-center justify-between px-3 py-2.5 hover:bg-coral-50/50 transition-colors"
      >
        <div className="flex items-center gap-2">
          <SentimentIcon size={14} className={sentimentColor} />
          <span className="text-xs font-medium text-neutral-700">Session Summary</span>
          <span className="text-[10px] text-neutral-400 flex items-center gap-1">
            <Clock size={10} />
            {minutes}m {seconds}s
          </span>
        </div>
        <div className="flex items-center gap-0.5">
          {expanded ? <ChevronUp size={14} className="text-neutral-400" /> : <ChevronDown size={14} className="text-neutral-400" />}
          {onDismiss && (
            <button
              onClick={(e) => { e.stopPropagation(); onDismiss(); }}
              className="p-0.5 text-neutral-400 hover:text-neutral-600 transition-colors"
              title="Dismiss summary"
            >
              <X size={12} />
            </button>
          )}
        </div>
      </button>

      {expanded && (
        <div className="px-3 pb-3 space-y-2">
          {/* Summary */}
          {data.summary && (
            <p className="text-xs text-neutral-600 leading-relaxed">{data.summary}</p>
          )}

          {/* Action Items */}
          {data.action_items.length > 0 && (
            <div className="space-y-1">
              <span className="text-[10px] font-medium text-neutral-500 uppercase tracking-wide">
                Action Items
              </span>
              {data.action_items.map((item, i) => (
                <div
                  key={i}
                  className="flex items-start gap-2 text-xs text-neutral-700 bg-white/60 rounded-lg px-2.5 py-1.5 border border-neutral-100"
                >
                  <CheckCircle2 size={12} className="text-coral-400 mt-0.5 flex-shrink-0" />
                  <span className="leading-relaxed">{item.text}</span>
                </div>
              ))}
            </div>
          )}

          {/* Success Score */}
          {data.success_score !== null && (
            <div className="flex items-center gap-2 text-[10px] text-neutral-500">
              <span>Confidence: {Math.round(data.success_score * 100)}%</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
